import { notifyUser } from './notificationService'
import { getDismissedEntityIds, getSnoozedEntityIds } from '../api/notifications'
import type { NotificationPreferences } from '../types/notifications'
import type { Task, ComplianceItem, CalendarEvent, Deal, Contact, Prospect, Client } from '../types'
import { formatDateOnlyOr } from './formatters'
import { parseDateOnly, startOfLocalDay } from './formatters'
import { plural } from './formatters'

/**
 * Check and create notifications for dashboard items.
 * Accepts optional preferences for configurable thresholds.
 */
export async function checkDashboardNotifications(
  userId: string,
  agencyId: string,
  data: {
    tasks: Task[]
    complianceItems: ComplianceItem[]
    events: CalendarEvent[]
    deals: Deal[]
    contacts: Contact[]
    prospects: Prospect[]
    clients?: Client[]
  },
  preferences?: NotificationPreferences | null
) {
  if (!agencyId) {
    console.warn('checkDashboardNotifications called without agencyId - skipping')
    return
  }

  // Batch-fetch dismissed/snoozed entities for performance
  const [dismissedEntities, snoozedEntities] = await Promise.all([
    getDismissedEntityIds(userId),
    getSnoozedEntityIds(userId),
  ])

  const now = new Date()
  const today = new Date(now.getFullYear(), now.getMonth(), now.getDate())

  // Configurable thresholds (fall back to defaults)
  const eventHours = preferences?.event_hours ?? 72
  const stalledDealDays = preferences?.stalled_deal_days ?? 30
  const followUpDays = preferences?.follow_up_days ?? 7

  const eventWindow = new Date(now.getTime() + eventHours * 60 * 60 * 1000)
  const stalledCutoff = new Date(now.getTime() - stalledDealDays * 24 * 60 * 60 * 1000)
  const followUpCutoff = new Date(now.getTime() - followUpDays * 24 * 60 * 60 * 1000)

  // Check tasks — batch overdue and due-today into at most 2 notifications
  const overdueTasks: Task[] = []
  const dueTodayTasks: Task[] = []
  data.tasks.forEach(task => {
    if (!task.due_date || task.status === 'completed' || task.status === 'cancelled') return
    const dueDate = parseDateOnly(task.due_date)
    if (!dueDate) return
    const itemDate = startOfLocalDay(dueDate)
    if (itemDate < today) overdueTasks.push(task)
    else if (itemDate.getTime() === today.getTime()) dueTodayTasks.push(task)
  })

  if (overdueTasks.length === 1) {
    notifyUser(userId, agencyId, {
      type: 'task',
      title: `Overdue Task: ${overdueTasks[0].title}`,
      message: `This task was due on ${formatDateOnlyOr(overdueTasks[0].due_date!)}`,
      priority: 'high',
      action_url: `/tasks/${overdueTasks[0].id}`,
      related_entity_type: 'task',
      related_entity_id: overdueTasks[0].id,
    }, dismissedEntities, snoozedEntities)
  } else if (overdueTasks.length > 1) {
    notifyUser(userId, agencyId, {
      type: 'task',
      title: `${overdueTasks.length} Overdue Tasks`,
      message: `You have ${overdueTasks.length} overdue tasks that need attention`,
      priority: 'high',
      action_url: `/tasks`,
      related_entity_type: 'task',
      related_entity_id: 'batch-tasks-overdue',
    }, dismissedEntities, snoozedEntities)
  }

  if (dueTodayTasks.length === 1) {
    notifyUser(userId, agencyId, {
      type: 'task',
      title: `Task Due Today: ${dueTodayTasks[0].title}`,
      message: `This task is due today`,
      priority: 'medium',
      action_url: `/tasks/${dueTodayTasks[0].id}`,
      related_entity_type: 'task',
      related_entity_id: dueTodayTasks[0].id,
    }, dismissedEntities, snoozedEntities)
  } else if (dueTodayTasks.length > 1) {
    notifyUser(userId, agencyId, {
      type: 'task',
      title: `${dueTodayTasks.length} Tasks Due Today`,
      message: `You have ${plural(dueTodayTasks.length, 'task')} due today`,
      priority: 'medium',
      action_url: `/tasks`,
      related_entity_type: 'task',
      related_entity_id: 'batch-tasks-today',
    }, dismissedEntities, snoozedEntities)
  }

  // Check compliance items — batch overdue and due-today into at most 2 notifications
  const overdueCompliance: ComplianceItem[] = []
  const dueTodayCompliance: ComplianceItem[] = []
  data.complianceItems.forEach(item => {
    if (!item.due_date || item.status === 'completed') return
    const dueDate = parseDateOnly(item.due_date)
    if (!dueDate) return
    const itemDate = startOfLocalDay(dueDate)
    if (itemDate < today) overdueCompliance.push(item)
    else if (itemDate.getTime() === today.getTime()) dueTodayCompliance.push(item)
  })

  if (overdueCompliance.length === 1) {
    notifyUser(userId, agencyId, {
      type: 'compliance',
      title: `Overdue Compliance: ${overdueCompliance[0].title}`,
      message: `This compliance item was due on ${formatDateOnlyOr(overdueCompliance[0].due_date!)}`,
      priority: 'urgent',
      action_url: `/compliance/${overdueCompliance[0].id}`,
      related_entity_type: 'task',
      related_entity_id: overdueCompliance[0].id,
    }, dismissedEntities, snoozedEntities)
  } else if (overdueCompliance.length > 1) {
    notifyUser(userId, agencyId, {
      type: 'compliance',
      title: `${overdueCompliance.length} Overdue Compliance Items`,
      message: `You have ${overdueCompliance.length} compliance items past due`,
      priority: 'urgent',
      action_url: `/compliance`,
      related_entity_type: 'task',
      related_entity_id: 'batch-compliance-overdue',
    }, dismissedEntities, snoozedEntities)
  }

  if (dueTodayCompliance.length === 1) {
    notifyUser(userId, agencyId, {
      type: 'compliance',
      title: `Compliance Due Today: ${dueTodayCompliance[0].title}`,
      message: `This compliance item is due today`,
      priority: 'high',
      action_url: `/compliance/${dueTodayCompliance[0].id}`,
      related_entity_type: 'task',
      related_entity_id: dueTodayCompliance[0].id,
    }, dismissedEntities, snoozedEntities)
  } else if (dueTodayCompliance.length > 1) {
    notifyUser(userId, agencyId, {
      type: 'compliance',
      title: `${dueTodayCompliance.length} Compliance Items Due Today`,
      message: `You have ${dueTodayCompliance.length} compliance items due today`,
      priority: 'high',
      action_url: `/compliance`,
      related_entity_type: 'task',
      related_entity_id: 'batch-compliance-today',
    }, dismissedEntities, snoozedEntities)
  }

  // Check upcoming events — batch into at most one notification
  const upcomingEvents = data.events.filter(event => {
    const startDate = new Date(event.start_date)
    return startDate > now && startDate <= eventWindow
  })
  if (upcomingEvents.length === 1) {
    const hoursUntil = Math.round((new Date(upcomingEvents[0].start_date).getTime() - now.getTime()) / (60 * 60 * 1000))
    notifyUser(userId, agencyId, {
      type: 'event',
      title: `Upcoming Event: ${upcomingEvents[0].title}`,
      message: `Starts in ${hoursUntil} hour${hoursUntil !== 1 ? 's' : ''}`,
      priority: 'medium',
      action_url: `/calendar`,
      related_entity_type: 'event',
      related_entity_id: upcomingEvents[0].id,
    }, dismissedEntities, snoozedEntities)
  } else if (upcomingEvents.length > 1) {
    notifyUser(userId, agencyId, {
      type: 'event',
      title: `${upcomingEvents.length} Upcoming Events`,
      message: `You have ${upcomingEvents.length} events in the next ${eventHours} hours`,
      priority: 'medium',
      action_url: `/calendar`,
      related_entity_type: 'event',
      related_entity_id: 'batch-events-upcoming',
    }, dismissedEntities, snoozedEntities)
  }

  // Check stalled deals — one batched notification instead of one per deal
  const stalledDeals = data.deals.filter(deal => {
    if (deal.stage === 'Lost' || deal.stage === 'Executed') return false
    return new Date(deal.updated) < stalledCutoff
  })
  if (stalledDeals.length === 1) {
    notifyUser(userId, agencyId, {
      type: 'deal',
      title: `Stalled Deal: ${stalledDeals[0].title}`,
      message: `No activity in over ${stalledDealDays} days`,
      priority: 'medium',
      action_url: `/deals/${stalledDeals[0].id}`,
      related_entity_type: 'deal',
      related_entity_id: stalledDeals[0].id,
    }, dismissedEntities, snoozedEntities)
  } else if (stalledDeals.length > 1) {
    notifyUser(userId, agencyId, {
      type: 'deal',
      title: `${stalledDeals.length} Stalled Deals`,
      message: `${plural(stalledDeals.length, 'deal')} ${stalledDeals.length === 1 ? 'has' : 'have'} had no activity in over ${plural(stalledDealDays, 'day')}`,
      priority: 'medium',
      action_url: `/deals`,
      related_entity_type: 'deal',
      related_entity_id: 'batch-stalled',
    }, dismissedEntities, snoozedEntities)
  }

  // Check follow-ups due — one batched notification per category
  const staleContacts = data.contacts.filter(c =>
    c.last_contact_date && new Date(c.last_contact_date) < followUpCutoff
  )
  if (staleContacts.length === 1) {
    const lastContact = new Date(staleContacts[0].last_contact_date!)
    const daysSince = Math.floor((now.getTime() - lastContact.getTime()) / (24 * 60 * 60 * 1000))
    notifyUser(userId, agencyId, {
      type: 'message',
      title: `Follow-up Due: ${staleContacts[0].first_name} ${staleContacts[0].last_name}`,
      message: `No contact in ${daysSince} day${daysSince !== 1 ? 's' : ''}`,
      priority: 'low',
      action_url: `/contacts/${staleContacts[0].id}`,
      related_entity_type: 'client',
      related_entity_id: staleContacts[0].id,
    }, dismissedEntities, snoozedEntities)
  } else if (staleContacts.length > 1) {
    notifyUser(userId, agencyId, {
      type: 'message',
      title: `${staleContacts.length} Contacts Need Follow-up`,
      message: `${staleContacts.length} contacts haven't been reached in over ${followUpDays} days`,
      priority: 'low',
      action_url: `/contacts`,
      related_entity_type: 'client',
      related_entity_id: 'batch-contacts',
    }, dismissedEntities, snoozedEntities)
  }

  const staleProspects = data.prospects.filter(p =>
    p.last_contact_at && new Date(p.last_contact_at) < followUpCutoff
  )
  if (staleProspects.length === 1) {
    const lastContact = new Date(staleProspects[0].last_contact_at!)
    const daysSince = Math.floor((now.getTime() - lastContact.getTime()) / (24 * 60 * 60 * 1000))
    notifyUser(userId, agencyId, {
      type: 'message',
      title: `Follow-up Due: ${staleProspects[0].full_name}`,
      message: `No contact in ${daysSince} day${daysSince !== 1 ? 's' : ''}`,
      priority: 'low',
      action_url: `/prospects/${staleProspects[0].id}`,
      related_entity_type: 'client',
      related_entity_id: staleProspects[0].id,
    }, dismissedEntities, snoozedEntities)
  } else if (staleProspects.length > 1) {
    notifyUser(userId, agencyId, {
      type: 'message',
      title: `${staleProspects.length} Prospects Need Follow-up`,
      message: `${plural(staleProspects.length, 'prospect')} ${staleProspects.length === 1 ? "hasn't" : "haven't"} been reached in over ${plural(followUpDays, 'day')}`,
      priority: 'low',
      action_url: `/prospects`,
      related_entity_type: 'client',
      related_entity_id: 'batch-prospects',
    }, dismissedEntities, snoozedEntities)
  }

  // ── Client milestones — batched per category ──
  if (data.clients && data.clients.length > 0) {
    const todayMd = `${String(today.getMonth() + 1).padStart(2, '0')}-${String(today.getDate()).padStart(2, '0')}`

    const birthdayClients: { client: Client; daysUntil: number }[] = []
    const expiringContracts: { client: Client; daysUntil: number }[] = []

    data.clients.forEach(client => {
      // Birthday: within 7 days
      const dob = parseDateOnly(client.date_of_birth)
      if (dob) {
        const thisYearBirthday = new Date(today.getFullYear(), dob.getMonth(), dob.getDate())
        const daysUntil = Math.ceil((thisYearBirthday.getTime() - today.getTime()) / 86400000)
        if (daysUntil >= 0 && daysUntil <= 7) {
          birthdayClients.push({ client, daysUntil })
        }
      }

      // Contract expiring within 30 days
      const endDate = parseDateOnly(client.contract_end_date)
      if (endDate) {
        const daysUntil = Math.ceil((endDate.getTime() - today.getTime()) / 86400000)
        if (daysUntil >= 0 && daysUntil <= 30) {
          expiringContracts.push({ client, daysUntil })
        }
      }

      // Draft anniversary (Jan 1 each year)
      if (client.draft_year) {
        const yearsSinceDraft = today.getFullYear() - client.draft_year
        if (yearsSinceDraft > 0 && todayMd === '01-01') {
          const clientId = (client as any).client_id || client.id
          notifyUser(userId, agencyId, {
            type: 'event',
            title: `Draft Anniversary: ${client.full_name}`,
            message: `${yearsSinceDraft} year${yearsSinceDraft !== 1 ? 's' : ''} since ${client.full_name} was drafted`,
            priority: 'low',
            action_url: `/clients/${clientId}`,
            related_entity_type: 'client',
            related_entity_id: clientId,
          }, dismissedEntities, snoozedEntities)
        }
      }
    })

    // Birthdays — batched
    if (birthdayClients.length === 1) {
      const { client, daysUntil } = birthdayClients[0]
      const clientId = (client as any).client_id || client.id
      notifyUser(userId, agencyId, {
        type: 'event',
        title: `Birthday: ${client.full_name}`,
        message: daysUntil === 0 ? `Today is ${client.full_name}'s birthday!` : `${client.full_name}'s birthday is in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
        priority: daysUntil <= 1 ? 'high' : 'medium',
        action_url: `/clients/${clientId}`,
        related_entity_type: 'client',
        related_entity_id: clientId,
      }, dismissedEntities, snoozedEntities)
    } else if (birthdayClients.length > 1) {
      notifyUser(userId, agencyId, {
        type: 'event',
        title: `${birthdayClients.length} Client Birthdays Coming Up`,
        message: `${birthdayClients.map(b => b.client.full_name).join(', ')} have birthdays in the next 7 days`,
        priority: 'medium',
        action_url: `/clients`,
        related_entity_type: 'client',
        related_entity_id: 'batch-birthdays',
      }, dismissedEntities, snoozedEntities)
    }

    // Expiring contracts — batched
    if (expiringContracts.length === 1) {
      const { client, daysUntil } = expiringContracts[0]
      const clientId = (client as any).client_id || client.id
      notifyUser(userId, agencyId, {
        type: 'contract',
        title: `Contract Expiring: ${client.full_name}`,
        message: daysUntil === 0 ? `${client.full_name}'s contract expires today` : `${client.full_name}'s contract expires in ${daysUntil} day${daysUntil !== 1 ? 's' : ''}`,
        priority: daysUntil <= 7 ? 'urgent' : 'high',
        action_url: `/clients/${clientId}`,
        related_entity_type: 'client',
        related_entity_id: clientId,
      }, dismissedEntities, snoozedEntities)
    } else if (expiringContracts.length > 1) {
      const soonest = Math.min(...expiringContracts.map(e => e.daysUntil))
      notifyUser(userId, agencyId, {
        type: 'contract',
        title: `${expiringContracts.length} Contracts Expiring Soon`,
        message: `${expiringContracts.map(e => e.client.full_name).join(', ')} have contracts expiring within 30 days`,
        priority: soonest <= 7 ? 'urgent' : 'high',
        action_url: `/clients`,
        related_entity_type: 'client',
        related_entity_id: 'batch-contracts-expiring',
      }, dismissedEntities, snoozedEntities)
    }
  }
}
